/**
 * Cart Product Gift Cleanup - Removes gift SKUs when their trigger products are removed
 *
 * This script monitors cart updates and automatically removes product-level gift SKUs
 * when their associated trigger products are no longer in the cart.
 */

class CartProductGiftCleanup {
  constructor() {
    this.isProcessing = false;
    this.init();
  }

  init() {
    // Listen for cart updates
    document.addEventListener('cart:updated', (event) => {
      // Skip cleanup for gift-related events to avoid interfering with cart state
      const source = event.detail?.source;
      if (source === 'gift-note' || source === 'product-gift') {
        console.log('CartProductGiftCleanup: Skipping for gift-related event:', source);
        return;
      }
      this.handleCartUpdate(event);
    });

    console.log('CartProductGiftCleanup initialized');
  }

  async handleCartUpdate(event) {
    // Prevent concurrent processing
    if (this.isProcessing) {
      console.log('CartProductGiftCleanup: Already processing, skipping');
      return;
    }

    // Add a small delay to ensure cart operations are complete
    await new Promise((resolve) => setTimeout(resolve, 300));

    this.isProcessing = true;

    try {
      // Get current cart state
      const cartData = await this.fetchCart();

      // Don't run cleanup if cart is empty
      if (!cartData || !cartData.items || cartData.items.length === 0) {
        console.log('CartProductGiftCleanup: Cart is empty, skipping cleanup');
        return;
      }

      // Check for orphaned gift SKUs
      const giftsToRemove = await this.findOrphanedGifts(cartData);

      if (giftsToRemove.length > 0) {
        console.log('CartProductGiftCleanup: Found orphaned gifts:', giftsToRemove);
        await this.removeOrphanedGifts(giftsToRemove, cartData);
      } else {
        console.log('CartProductGiftCleanup: No orphaned gifts found');
      }
    } catch (error) {
      console.error('CartProductGiftCleanup: Error processing cart update:', error);
    } finally {
      this.isProcessing = false;
    }
  }

  async fetchCart() {
    const response = await fetch('/cart.js');
    return response.json();
  }

  async findOrphanedGifts(cartData) {
    const orphanedGifts = [];

    // Get gift configuration from settings
    const giftConfig = window.productGiftConfigs || [];

    if (giftConfig.length === 0) {
      return orphanedGifts;
    }

    // Build a map of trigger products currently in cart
    const triggerProductsInCart = new Set();
    cartData.items.forEach((item) => {
      // Only count non-gift items as trigger products
      if (!item.properties || item.properties._product_gift !== 'true') {
        triggerProductsInCart.add(item.product_id);
      }
    });

    console.log('CartProductGiftCleanup: Trigger products in cart:', Array.from(triggerProductsInCart));

    // Check each cart item to see if it's a gift SKU without its trigger
    cartData.items.forEach((item, index) => {
      // Check if this item is a product gift SKU
      if (item.properties && item.properties._product_gift === 'true') {
        // Find which trigger product this gift belongs to
        const giftEntry = giftConfig.find((config) => config.giftVariantId == item.variant_id);

        if (giftEntry) {
          console.log(
            `CartProductGiftCleanup: Checking gift ${item.variant_id}, needs trigger ${giftEntry.triggerProductId}`
          );

          // This is a gift SKU - check if its trigger is in cart
          if (!triggerProductsInCart.has(giftEntry.triggerProductId)) {
            console.log(
              `CartProductGiftCleanup: Gift SKU ${item.variant_id} has no trigger in cart - marking for removal`
            );
            orphanedGifts.push({
              key: item.key, // Use key instead of line index for more reliable removal
              variantId: item.variant_id,
              productId: item.product_id,
              title: item.title,
              triggerProductId: giftEntry.triggerProductId,
            });
          } else {
            console.log(`CartProductGiftCleanup: Gift SKU ${item.variant_id} has trigger in cart - keeping`);
          }
        }
      }
    });

    return orphanedGifts;
  }

  async removeOrphanedGifts(giftsToRemove, cartData) {
    console.log('CartProductGiftCleanup: Removing orphaned gifts...', giftsToRemove);

    // Show loading overlay
    const cartDrawer = document.querySelector('cart-drawer');
    if (cartDrawer && cartDrawer.showSimpleLoading) {
      cartDrawer.showSimpleLoading();
    }

    try {
      // Remove gifts using their keys (more reliable than line indices)
      for (const gift of giftsToRemove) {
        console.log(`CartProductGiftCleanup: Removing gift with key ${gift.key}: ${gift.title}`);

        await fetch('/cart/change.js', {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
          },
          body: JSON.stringify({
            id: gift.key,
            quantity: 0,
          }),
        });
      }

      // Refresh cart drawer after all removals
      const cartDrawerItems = document.querySelector('cart-drawer-items');
      if (cartDrawerItems && cartDrawerItems.onCartUpdate) {
        setTimeout(() => {
          cartDrawerItems.onCartUpdate();
        }, 200);
      } else if (cartDrawer && cartDrawer.hideSimpleLoading) {
        // Fallback: hide loading if cart drawer items not found
        setTimeout(() => {
          cartDrawer.hideSimpleLoading();
        }, 500);
      }

      console.log('CartProductGiftCleanup: Successfully removed orphaned gifts');
    } catch (error) {
      console.error('CartProductGiftCleanup: Error removing orphaned gifts:', error);

      // Hide loading on error
      if (cartDrawer && cartDrawer.hideSimpleLoading) {
        cartDrawer.hideSimpleLoading();
      }
    }
  }
}

// Initialize when DOM is ready
if (document.readyState === 'loading') {
  document.addEventListener('DOMContentLoaded', () => {
    window.cartProductGiftCleanup = new CartProductGiftCleanup();
  });
} else {
  window.cartProductGiftCleanup = new CartProductGiftCleanup();
}
